import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  tap,
} from 'rxjs';
import { Pari } from '../../models/pari.model';
import { Game, GameFormula } from '../../models/game.model';
// import { ACTIVE_GAMES_RESP_DATA } from '../../sample-data/active-games.data';
import { Meeting } from '../../models/meeting.model';
import { Race } from '../../models/race.model';
import { GetProviderReqParams, Operator, Provider, ProviderType } from '../../models/provider.model';
import { HttpService } from '../helper/http.service';
import { environment } from '../../../environments/environment';
import { AccountEndpoints, TurfEndpoints } from '../../../configs/api.config';
import { ActiveGamesRespData, ActiveRace, Country } from '../../models/active-games-resp.model';
import { BetPayOptions } from '../../models/save-bet-options.model';
import { Partant } from '../../models/partant.model';
import { OverlayLoaderOption } from '../../models/overlay-loader.model';

@Injectable({
  providedIn: 'root',
})
export class PmuService {
  private readonly jsonData = new BehaviorSubject<ActiveGamesRespData | null>(
    null
  );

  private readonly dataToSubmitSubject = new BehaviorSubject<Pari>({
    meeting: {} as Meeting,
    race: {} as Race,
    prize: 0,
    type: '',
    chevalBases: [],
    chevalAssocies: [],
    mise: 0,
    coefficient:0,
    montant: 0,
    payable: 0,
    time: '',
    game: {} as Game,
    nCombine: 0,
    completeFormula: false,
    spotEnabled: false,
    multi: 0,
    sformula: '',
  });

  private readonly betOptionsSubject = new BehaviorSubject<BetPayOptions> ({
    operators: [],
    countries: [],
    deviceInfo: {},
    providers: []
  });

  private readonly paymentProviderSubject = new BehaviorSubject<Record<ProviderType, Provider[]>> ({
    "CASHIN" : [], "CASHOUT": []
  })

  overLayLoader$ = new BehaviorSubject<boolean>(false);
  overLayOptions$ = new BehaviorSubject<Partial<OverlayLoaderOption>>({});

  data$ = this.dataToSubmitSubject.asObservable();
  betOptions$ = this.betOptionsSubject.asObservable();
  paymentProviders$ = this.paymentProviderSubject.asObservable();
  refreshAppState = new BehaviorSubject<boolean>(false);
  constructor(private readonly httpService: HttpService) {}

  getActiveGames(eventData?: any) {
    let params: any = {
      channel: 7,
      versioncode: 3,
      date_jr: eventData
        ? new Intl.DateTimeFormat('en-US').format(eventData)
        : '',
    };
    const [gw, api] = [
      environment.API.turfGateway,
      TurfEndpoints.getActiveGames,
    ];
    // this.jsonData.next(ACTIVE_GAMES_RESP_DATA);
    // return;
    return this.httpService
      .getNoAuth<ActiveGamesRespData>(gw, api, params)
      .pipe(tap((res) => res.hasOwnProperty('data')))
      .subscribe((res: any) => {
        const resp = {...res.data };
        if ('HP' in resp) {
          this.jsonData.next(resp);
        }
      });
  }

  getBetOptionsData(){
    return this.betOptionsSubject.getValue();
  }

  getCurrentData() {
    return this.dataToSubmitSubject.getValue();
  }

  getACData(): Observable<{ operators: Partial<Operator[]> | null; countries: Country[] | null }> {
    return this.jsonData.pipe(
      map((data) => ({
        operators: (!data || !data.AC.length) ? null : (data.AC as Array<Partial<Operator[]>>)[0].flat(),
        countries: (!data || !data.AC.length) ? null : (data.AC as Array<Country[]>)[2].flat()
      }))
    );
  }


  getOperatorsFromAC(): Observable<Partial<Operator[]> | null> {
    return this.jsonData.pipe(
      map((data) =>
        !data || !data.AC.length
          ? null
          : (data.AC as Array<Partial<Operator[]>>)[0].flat()
      )
    );
  }

  getCountriesFromAC(): Observable<Country[] | null> {
    return this.jsonData.pipe(
      map((data) => (
        (!data || !data.AC.length) ? null :
        (data.AC as Array<Country[]>)[2].flat()
      ))
    );
  }

  getMeetings(): Observable<Meeting[]> {
    return this.jsonData.pipe(
      filter((response) => response !== null),
      map((response) => {
        return response.HP.map((r) => {
          let races = r.races.map((c) => this.mapRaceDate(c, r.nReunion));

          return {
            id: r.nReunion,
            name: r.libelle,
            date: r.rd,
            races: races,
          };
        });
      })
    );
  }

  getRaces(meetingId: number): Observable<Race[]> {
    return this.jsonData.pipe(
      filter((response) => response !== null),
      map((response) => {
        let races: Race[] = [];
        let responseRaces = response.HP.find(
          (r) => r.nReunion === meetingId
        )?.races;
        if (responseRaces) {
          races = responseRaces.map((r) => this.mapRaceDate(r, meetingId));
        }
        return races;
      })
    );
  }

  private mapRaceDate(race: ActiveRace, meetingId: number): Race {
    let horses: Partant[] = (race.partants).map((p) => ({
      ...p,
      selectionne: false,
    }));
    const regex = /^(?!Non-).*/;
    return {
      id: race.course_number,
      name: race.libelle,
      date: race.racedt,
      type: race.particularite,
      competitorsCount: race.partants.flatMap(p => !regex.test(p.status)).length,
      meetingId,
      distance: race.distance,
      horses: horses,
      partantNumber: +race.partantnb,
      numero: +race.numero
    };
  }

  getGames(meetingId: number, raceId: number): Observable<Game[]> {
    return this.jsonData.pipe(
      filter((response) => response !== null),
      map((response) => {
        const races = response.HP.find((r) => r.nReunion === meetingId)?.races;
        let responseRace = races?.find((r) => r.course_number === raceId);
        let games: Game[] = [];
        if (responseRace) {
          let gameLabels = responseRace.libelleJeux;
          let gamesCompleteFormula = responseRace.formulaControl;
          games = responseRace.jeu.split('@').map((gameCipher) => {
            let params = gameCipher.split('-');

            return {
              code: params[0],
              name: gameLabels[params[0]],
              raceId: raceId,
              meetingId: meetingId,
              baseStake: parseInt(params[1]),
              formulas: <GameFormula[]>params[4].trim()?.replace(/ /g, '')?.split(',') ?? [],
              formulaControl:
                gamesCompleteFormula[params[0]]?.b_control_formule ?? false,
              maxCoefficient: parseInt(params[2]),
              baseHorseCount: parseInt(params[3]),
              color: params[5].toLowerCase(),
              hasOrder: parseInt(params[6]) === 1,
              // flexi: parseInt(params[7]),
              flexi: 0,
              spot: {
                enabled: params[8]?.includes('|') ?? false,
                maxHorseCount: parseInt(params[8]?.split('|')[0] ?? '0'),
                formulas:
                  <GameFormula[]>params[8]?.split('|')[1]?.trim()?.replace(/ /g, '')?.split(',') ?? [],
              },
            };
          });
        }
        return games;
      })
    );
  }

  updateData(newData: Partial<Pari>) {
    const currentData = this.dataToSubmitSubject.value;
    this.dataToSubmitSubject.next({ ...currentData, ...newData });
  }

  updateBetOptions(optionsField: Partial<BetPayOptions>) {
    const currentData = this.betOptionsSubject.getValue();
    this.betOptionsSubject.next({...currentData, ...optionsField});
  }

// Helper function to get list of non partant horses from a race
  getNonPartantHorses(race: Race): Partant[] {
    return race.horses?.filter(horse => horse.status === 'Non-Partant');
  }


  resetBetData(){
    const currentData = {
      ...this.dataToSubmitSubject.value,
      price: 0,

    };
  }

   fetchCashInProviders(params: GetProviderReqParams){
    const hostApi = environment.API.turfGateway, endpoint = AccountEndpoints.paymentProviders;

    return this.httpService.getNoAuth( hostApi, endpoint, params)
      .pipe(tap(res => res.hasOwnProperty('providers')))
      .subscribe((data: any) => {
        const currentValue = this.paymentProviderSubject.value;
        const updatedValue = {
          ...currentValue,
          [params.type]: data['providers'],
        };
        this.paymentProviderSubject.next(updatedValue);
      }
    )

  }
}
